import { supabase } from '@/lib/supabaseClient';
import { ActivityType } from '@/types/activity';

interface LogActivityParams {
  action_type: ActivityType;
  user_id?: string;
  project_id?: string;
  organization_id?: string;
  tenant_id?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  details?: Record<string, any>;
  impersonated_by_user_id?: string;
  impersonated_by_email?: string;
}

/**
 * Log an activity to the activity_logs table
 * Includes retry logic and timestamp precision to prevent race conditions
 */
export const logActivity = async ({
  action_type,
  user_id,
  project_id,
  organization_id,
  tenant_id,
  related_entity_type,
  related_entity_id,
  details,
  impersonated_by_user_id,
  impersonated_by_email
}: LogActivityParams) => {
  try {
    let detectedImpersonatedBy = impersonated_by_user_id;
    let detectedImpersonatedByEmail = impersonated_by_email;
    
    // Get current user if not provided
    if (!user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      user_id = user?.id;
      
      // Auto-detect impersonation if not explicitly provided
      if (!detectedImpersonatedBy && user?.user_metadata?.impersonating && user?.user_metadata?.original_user_id) {
        detectedImpersonatedBy = user.user_metadata.original_user_id;
        detectedImpersonatedByEmail = user.user_metadata.original_user_email;
        console.log(`[ActivityLogger] Detected impersonation context:`, {
          impersonated_by_user_id: detectedImpersonatedBy,
          impersonated_by_email: detectedImpersonatedByEmail
        });
      } else {
        console.log(`[ActivityLogger] No impersonation detected. User metadata:`, user?.user_metadata);
      }
    }

    // Use high-precision timestamp to prevent race conditions
    const timestamp = new Date().toISOString();

    const { error } = await supabase
      .from('activity_logs')
      .insert({
        action_type,
        user_id,
        project_id,
        organization_id,
        tenant_id,
        related_entity_type,
        related_entity_id,
        details,
        impersonated_by_user_id: detectedImpersonatedBy,
        impersonated_by_email: detectedImpersonatedByEmail,
        timestamp
      });

    if (error) {
      console.error('Error logging activity:', error);
      
      // Handle specific error codes
      if (error.code === '23505') { // Unique constraint violation
        // Retry once with a slightly different timestamp
        const retryTimestamp = new Date(Date.now() + 1).toISOString();
        const { error: retryError } = await supabase
          .from('activity_logs')
          .insert({
            action_type,
            user_id,
            project_id,
            organization_id,
            tenant_id,
            related_entity_type,
            related_entity_id,
            details,
            impersonated_by_user_id: detectedImpersonatedBy,
            impersonated_by_email: detectedImpersonatedByEmail,
            timestamp: retryTimestamp
          });
        
        if (retryError) {
          console.error('Error on retry logging activity:', retryError);
        }
      } else if (error.code === '25P02') { // Transaction aborted
        console.warn('Transaction aborted, skipping activity log:', error.message);
        // Don't retry for transaction errors as they indicate a larger issue
      } else if (error.code === '42P01') { // Relation does not exist
        console.warn('Activity logs table not accessible, skipping log:', error.message);
        // Don't retry for missing table errors
      } else {
        // For other errors, try one more time with a fresh connection
        console.warn('Retrying activity log with fresh connection...');
        const { error: retryError } = await supabase
          .from('activity_logs')
          .insert({
            action_type,
            user_id,
            project_id,
            organization_id,
            tenant_id,
            related_entity_type,
            related_entity_id,
            details,
            impersonated_by_user_id: detectedImpersonatedBy,
            impersonated_by_email: detectedImpersonatedByEmail,
            timestamp: new Date().toISOString()
          });
        
        if (retryError) {
          console.error('Error on final retry logging activity:', retryError);
        }
      }
    }
  } catch (error) {
    console.error('Error logging activity:', error);
    // Don't throw the error - activity logging should never break the main functionality
  }
};

/**
 * Log user management activities
 */
export const logUserActivity = {
  created: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'user_created',
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  updated: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'user_updated',
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  deleted: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'user_deleted',
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  restored: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'user_restored',
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  login: async (userId: string) => {
    await logActivity({
      action_type: 'login',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId
    });
  },

  logout: async (userId: string) => {
    await logActivity({
      action_type: 'logout',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId
    });
  },

  passwordChanged: async (userId: string) => {
    await logActivity({
      action_type: 'password_changed',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId
    });
  },

  roleChanged: async (userId: string, oldRole: string, newRole: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'user_role_changed',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details: {
        old_role: oldRole,
        new_role: newRole,
        ...details
      }
    });
  },

  passwordResetRequested: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'password_reset_requested',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  passwordResetCompleted: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'password_reset_completed',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  emailVerified: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'email_verified',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  accountLocked: async (userId: string, reason?: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'account_locked',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details: {
        reason,
        ...details
      }
    });
  },

  accountUnlocked: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'account_unlocked',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  },

  failedLoginAttempt: async (email: string, reason?: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'failed_login_attempt',
      related_entity_type: 'user',
      related_entity_id: email,
      details: {
        email,
        reason,
        ...details
      }
    });
  },

  impersonationStarted: async (targetUserId: string, targetUserEmail: string, originalUserId: string, originalUserEmail: string, tenantId?: string) => {
    await logActivity({
      action_type: 'impersonation_started',
      user_id: originalUserId,
      tenant_id: tenantId,
      related_entity_type: 'user',
      related_entity_id: targetUserId,
      details: {
        target_user_id: targetUserId,
        target_user_email: targetUserEmail,
        original_user_id: originalUserId,
        original_user_email: originalUserEmail
      }
    });
  },

  impersonationEnded: async (targetUserId: string, targetUserEmail: string, originalUserId: string, originalUserEmail: string, tenantId?: string) => {
    await logActivity({
      action_type: 'impersonation_ended',
      user_id: originalUserId,
      tenant_id: tenantId,
      related_entity_type: 'user',
      related_entity_id: targetUserId,
      details: {
        target_user_id: targetUserId,
        target_user_email: targetUserEmail,
        original_user_id: originalUserId,
        original_user_email: originalUserEmail
      }
    });
  }
};

/**
 * Log team management activities
 */
export const logTeamActivity = {
  created: async (teamId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'team_created',
      related_entity_type: 'team',
      related_entity_id: teamId,
      details
    });
  },

  updated: async (teamId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'team_updated',
      related_entity_type: 'team',
      related_entity_id: teamId,
      details
    });
  },

  deleted: async (teamId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'team_deleted',
      related_entity_type: 'team',
      related_entity_id: teamId,
      details
    });
  },

  memberAdded: async (teamId: string, userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'team_member_added',
      related_entity_type: 'team',
      related_entity_id: teamId,
      details: {
        ...details,
        added_user_id: userId
      }
    });
  }
};

/**
 * Log installer management activities
 */
export const logInstallerActivity = {
  created: async (installerId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'installer_created',
      related_entity_type: 'installer',
      related_entity_id: installerId,
      details
    });
  },

  updated: async (installerId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'installer_updated',
      related_entity_type: 'installer',
      related_entity_id: installerId,
      details
    });
  },

  deleted: async (installerId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'installer_deleted',
      related_entity_type: 'installer',
      related_entity_id: installerId,
      details
    });
  }
};

/**
 * Log settings activities
 */
export const logSettingsActivity = {
  updated: async (settingName: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'settings_updated',
      related_entity_type: 'settings',
      related_entity_id: settingName,
      details
    });
  }
};

/**
 * Log project assignment activities
 */
export const logProjectActivity = {
  assigned: async (projectId: string, userId: string, role?: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'project_assigned',
      project_id: projectId,
      user_id: userId,
      related_entity_type: 'project',
      related_entity_id: projectId,
      details: {
        assigned_user_id: userId,
        role,
        ...details
      }
    });
  },

  unassigned: async (projectId: string, userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'project_unassigned',
      project_id: projectId,
      user_id: userId,
      related_entity_type: 'project',
      related_entity_id: projectId,
      details: {
        unassigned_user_id: userId,
        ...details
      }
    });
  },

  fileDownloaded: async (projectId: string, fileName: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'file_downloaded',
      project_id: projectId,
      related_entity_type: 'document',
      related_entity_id: fileName,
      details: {
        file_name: fileName,
        ...details
      }
    });
  }
};

/**
 * Log task activities
 */
export const logTaskActivity = {
  created: async (taskId: string, projectId?: string, assigneeId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_created',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        assignee_id: assigneeId,
        ...details
      }
    });
  },

  assigned: async (taskId: string, assigneeId: string, projectId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_assigned',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        assignee_id: assigneeId,
        ...details
      }
    });
  },

  reassigned: async (taskId: string, oldAssigneeId: string, newAssigneeId: string, projectId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_reassigned',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        old_assignee_id: oldAssigneeId,
        new_assignee_id: newAssigneeId,
        ...details
      }
    });
  },

  updated: async (taskId: string, projectId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_updated',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        ...details
      }
    });
  },

  statusChanged: async (taskId: string, oldStatus: string, newStatus: string, projectId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_status_changed',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        old_status: oldStatus,
        new_status: newStatus,
        ...details
      }
    });
  },

  completed: async (taskId: string, projectId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_completed',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        ...details
      }
    });
  },

  deleted: async (taskId: string, projectId?: string, details?: Record<string, any>, tenantId?: string | null) => {
    await logActivity({
      action_type: 'task_deleted',
      project_id: projectId,
      tenant_id: tenantId || undefined,
      related_entity_type: 'task',
      related_entity_id: taskId,
      details: {
        task_id: taskId,
        ...details
      }
    });
  }
};

/**
 * Log system activities
 */
export const logSystemActivity = {
  searchPerformed: async (searchTerm: string, resultsCount?: number, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'search_performed',
      related_entity_type: 'system',
      related_entity_id: 'search',
      details: {
        search_term: searchTerm,
        results_count: resultsCount,
        ...details
      }
    });
  },

  dataExported: async (exportType: string, recordCount?: number, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'data_exported',
      related_entity_type: 'system',
      related_entity_id: 'export',
      details: {
        export_type: exportType,
        record_count: recordCount,
        ...details
      }
    });
  },

  bulkOperation: async (operationType: string, affectedCount: number, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'bulk_operation',
      related_entity_type: 'system',
      related_entity_id: 'bulk',
      details: {
        operation_type: operationType,
        affected_count: affectedCount,
        ...details
      }
    });
  },

  sessionExpired: async (userId: string, details?: Record<string, any>) => {
    await logActivity({
      action_type: 'session_expired',
      user_id: userId,
      related_entity_type: 'user',
      related_entity_id: userId,
      details
    });
  }
};

/**
 * Log dashboard activities (DASH-23.2)
 * Logs cross-tenant sharing, template operations, and permission changes
 */
export const logDashboardActivity = {
  /**
   * Log dashboard shared to another tenant
   * Fire-and-forget - does not await result
   */
  shared: (
    dashboardId: string,
    sourceTenantId: string,
    targetTenantId: string,
    targetTenantName: string,
    newDashboardId: string,
    details?: Record<string, any>
  ) => {
    // Fire-and-forget - don't await to avoid blocking
    logActivity({
      action_type: 'dashboard_shared',
      related_entity_type: 'dashboard',
      related_entity_id: dashboardId,
      tenant_id: sourceTenantId,
      details: {
        source_tenant_id: sourceTenantId,
        target_tenant_id: targetTenantId,
        target_tenant_name: targetTenantName,
        new_dashboard_id: newDashboardId,
        ...details
      }
    });
  },

  /**
   * Log dashboard permissions changed
   * Fire-and-forget - does not await result
   */
  permissionsChanged: (
    dashboardId: string,
    tenantId: string,
    permissionType: string,
    details: {
      affected_users?: string[];
      affected_roles?: number[];
      can_edit?: boolean;
      can_view?: boolean;
      action?: 'created' | 'updated' | 'reset_to_private';
      previous_permission_type?: string;
    }
  ) => {
    // Fire-and-forget - don't await to avoid blocking
    logActivity({
      action_type: 'dashboard_permissions_changed',
      related_entity_type: 'dashboard',
      related_entity_id: dashboardId,
      tenant_id: tenantId,
      details: {
        permission_type: permissionType,
        ...details
      }
    });
  },

  /**
   * Log widget saved as template
   * Fire-and-forget - does not await result
   */
  templateSaved: (
    templateId: string,
    tenantId: string,
    details: {
      template_name: string;
      source_widget_id?: string;
      source_dashboard_id?: string;
      category?: string;
    }
  ) => {
    // Fire-and-forget - don't await to avoid blocking
    logActivity({
      action_type: 'widget_template_saved',
      related_entity_type: 'widget_template',
      related_entity_id: templateId,
      tenant_id: tenantId,
      details
    });
  },

  /**
   * Log template shared to other tenants
   * Fire-and-forget - does not await result
   */
  templateShared: (
    templateId: string,
    sourceTenantId: string,
    details: {
      template_name: string;
      target_tenant_ids: string[];
      target_tenant_names?: string[];
      mapping_summary?: string;
    }
  ) => {
    // Fire-and-forget - don't await to avoid blocking
    logActivity({
      action_type: 'widget_template_shared',
      related_entity_type: 'widget_template',
      related_entity_id: templateId,
      tenant_id: sourceTenantId,
      details
    });
  }
}; 